import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useTenantStore } from '../../stores/useTenantStore';
import { useMedicalRequestStore } from '../../stores/useMedicalRequestStore';
import { useAdminRequestStore } from '../../stores/useAdminRequestStore';
import { useRepeatPrescriptionRequestStore } from '../../stores/useRepeatPrescriptionRequestStore';
import useErrorNavigation from './useErrorNavigation';
import { fetchNhsLoginUrlApi } from '../../services/nhs.service';
import {
  NhsLoginAuthorizeResponse,
  RequestType,
  RequestTypeBroad,
  RequestTypeShort,
} from '../../types';

type NhsStateDecoded = {
  requestType: RequestTypeBroad;
  tenantId?: string | null;
};

type VerifyResult = {
  ok: boolean;
  errors?: any;
};

type NhsLoginStartOptions = {
  onSuccess?: (ctx: { requestType: RequestTypeBroad }) => void | Promise<void>;
  onNhsError?: (stx: { error: string; description?: string }) => void | Promise<void>;
};

function safeDecodeState(rawState: unknown): NhsStateDecoded | null {
  if (!rawState || typeof rawState !== 'string') return null;

  try {
    const decodedString = atob(rawState);
    const decoded = JSON.parse(decodedString) as {
      tenantId: string;
      requestType: RequestTypeShort | RequestType;
    };

    const requestType = decoded?.requestType;
    const tenantId = decoded?.tenantId;
    if (
      requestType !== 'medical' &&
      requestType !== 'admin' &&
      requestType !== 'repeat-prescription'
    ) {
      return null;
    }

    return { requestType: `${requestType}-request`, tenantId: tenantId ?? null };
  } catch {
    return null;
  }
}

export function useNhsLogin(deps: NhsLoginStartOptions = {}) {
  const route = useRoute();
  const router = useRouter();

  const started = ref(false);
  const nhsLoginError = computed(() => route.query?.Error as string | undefined);
  const nhsLoginErrorDescription = computed(
    () => route.query?.error_description as string | undefined,
  );

  const tenantStore = useTenantStore();
  const errorNavigation = useErrorNavigation();

  const nhsLoginCode = computed(() => route.query?.code as string | undefined);
  const nhsStateRaw = computed(() => route.query?.state);

  const medicalRequestStore = useMedicalRequestStore();
  const adminRequestStore = useAdminRequestStore();
  const repeatPrescriptionRequestStore = useRepeatPrescriptionRequestStore();

  async function checkForNhsErrorParams() {
    if (!nhsLoginError.value) return;

    const decodedState = safeDecodeState(nhsStateRaw.value);
    const tenantId = decodedState?.tenantId;

    console.error('NHS login ERROR', tenantId);

    if (tenantId) {
      const pathname = window.location.pathname;
      const alreadyPrefixed = pathname === `/${tenantId}/` || pathname.startsWith(`/${tenantId}/`);

      if (!alreadyPrefixed) {
        // add tenant to BASE and redirect to tenant-error route
        try {
          await router.replace({
            name: 'tenant-error',
            params: { tenantId },
          });
        } catch (e) {
          console.error('router.replace failed', e);
        }
        return;
      }
    } else {
      // issues with teh NHS Error falls to base error
      await router.replace({
        name: 'error-main',
      });
    }
  }

  onMounted(async () => checkForNhsErrorParams());

  function getStoreForRequestType(requestType: RequestType) {
    if (requestType === 'medical-request') return medicalRequestStore;
    if (requestType === 'admin-request') return adminRequestStore;
    return repeatPrescriptionRequestStore;
  }

  const hasRun = ref(false);

  async function start() {
    if (started.value) return true;
    started.value = true;

    const err = nhsLoginError.value;
    if (err) {
      await deps.onNhsError?.({ error: err, description: nhsLoginErrorDescription.value });
      return false;
    }
    watch(
      () => [nhsLoginCode.value, nhsStateRaw.value] as const,
      async ([code, stateRaw]) => {
        if (!started.value) return;
        if (hasRun.value) return;
        if (!code || !stateRaw) return;

        hasRun.value = true;

        await run({
          code,
          decodedState: safeDecodeState(stateRaw),
        });
      },
      { immediate: true },
    );
  }

  async function run(params: { code: string; decodedState: any }): Promise<void> {
    const { code, decodedState } = params;

    if (!decodedState) {
      await errorNavigation.navigateToError({
        message: 'Sorry but something went wrong. Invalid state.',
      });
      return;
    }

    const { requestType, tenantId } = decodedState;

    if (!requestType) {
      await errorNavigation.navigateToError({
        message: 'Sorry but something went wrong. Missing request type.',
      });
      return;
    }

    if (!tenantId) {
      await errorNavigation.navigateToError({
        message: 'Sorry but something went wrong. Missing tenant.',
      });
      return;
    }

    await tenantStore.fetchTenant(tenantId);
    const store = getStoreForRequestType(requestType);

    // NB. check nhs code against our stored session (BE)
    const verifyNhsCode = (await (store as any).verifyNhsLogin(code)) as VerifyResult;

    if (verifyNhsCode?.ok) {
      // NSH login success.
      // callback functions here
      if (deps.onSuccess) {
        await deps.onSuccess({ requestType });
      }
      return;
    }

    await errorNavigation.navigateToError(
      {
        message: `Sorry but something went wrong. Request type "${requestType}" doesn\'t exist or does not match.`,
      },
      verifyNhsCode?.errors,
    );
  }

  function constructNhsLoginUrl(res: NhsLoginAuthorizeResponse): string {
    const { authUrl, client_id, redirect_uri, response_type, scope, state, nonce, vtr } = res.loginRequest;
    const url = new URL(authUrl);
    url.searchParams.set('response_type', response_type);
    url.searchParams.set('scope', scope);
    url.searchParams.set('client_id', client_id);
    url.searchParams.set('redirect_uri', `${redirect_uri}`);
    url.searchParams.set('state', state);
    url.searchParams.set('nonce', nonce);
    url.searchParams.set('vtr', vtr);

    return url.toString();
  }

  async function getPortalLink(requestType: RequestTypeShort): Promise<string> {
    const result = await fetchNhsLoginUrlApi(requestType);

    console.log('getPortalLink result', result);

    if (!result.ok) {
      await errorNavigation.navigateToError({
        message:
          'Sorry but something went wrong getting the NHS login url. Please try again later.',
        errors: result.errors,
      });
      throw new Error('Failed to get NHS login URL');
    } else {
      return constructNhsLoginUrl(result.data);
    }
  }

  return {
    getPortalLink,
    nhsLoginCode,
    start,
  };
}
